// utilities
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// redux functions
import { selectDokumenKerjasama } from "../../reducers/dokumenKerjasama/dokumenKerjasama.actions";

// constants and data types
import { RootState } from "../../store/rootReducer";
import { PartnerRequest } from "../../interfaces/Partner";
import { DepartmentRequest } from "../../interfaces/Department";
import { DokumenKerjasamaRequest } from "../../interfaces/DokumenKerjasama";
import moment from "moment";
import { initializeCreateMonev } from "../../reducers/monev/monev.actions";
import { PermissionMenuName } from "../../interfaces/UserRole";
import { useState } from "react";
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import Datatables from "../../layouts/Datatables";
import { Tooltip } from '@mui/material';
import Zoom from '@mui/material/Zoom';

export type DokumenKerjasamaListPageListProps = {
  refresh: () => void;
}

/**
 * Dokumen Kerjasama List Page: List
 * Show list of dokumen kerjasama based on filter set up
 * at DokumenKerjasamaListPageFilter. listDokumenKerjasama
 * is retrieved from API which is called on the
 * DokumenKerjasamaListPage (main component).
 */
const DokumenKerjasamaListPageList = (props: DokumenKerjasamaListPageListProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  
  const isLoading = useSelector((state: RootState) => state.dokumenKerjasama.isLoading);
  const errorMessage = useSelector((state: RootState) => state.dokumenKerjasama.errorMessage);
  const listDokumenKerjasama = useSelector((state: RootState) => state.dokumenKerjasama.listDokumenKerjasama);
  const roleMonev = useSelector((state: RootState) => state.authorization.systemAccess[PermissionMenuName.monevPartner].ACCESS);

  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  /**
   * Redirect user to edit selected document.
   * @param dokumenKerjasama selected document
   */
  const editDokumenKerjasama = (dokumenKerjasama: DokumenKerjasamaRequest) => {
    dispatch(selectDokumenKerjasama(dokumenKerjasama));
    history.push(`/dokumen-kerjasama/${dokumenKerjasama.DOK_ID}`);
  }

  /**
   * Redirect user to monev page of selected document.
   * @param dokumenKerjasama selected document
   */
   const editMonev = (dokumenKerjasama: DokumenKerjasamaRequest) => {
    dispatch(initializeCreateMonev(dokumenKerjasama.DOK_ID));
    history.push(`/monev/${dokumenKerjasama.DOK_ID}/create`);
  }

  /**
   * Renders company string for selected document
   * for the table display.
   * @param list of companies on selected document
   * @returns '<first company name> and x others'
   */
  const renderCompanyCount = (coDetail: PartnerRequest[]) => {
    if (coDetail.length > 0) {
      if (coDetail.length === 1) return `${coDetail[0].CO_NAME}`;
      return `${coDetail[0].CO_NAME} dan ${coDetail.length - 1} lainnya`;
    } else {
      return 'Tidak ada partner';
    }
  }

  /**
   * Renders department string for selected document
   * for the table display.
   * @param list of departments on selected document
   * @returns '<first department name> and x others'
   */
  const renderDepartmentCount = (deptDetail: DepartmentRequest[]) => {
    if (deptDetail.length > 0) {
      if (deptDetail.length === 1) return `${deptDetail[0].DEPT_NAME}`;
      return `${deptDetail[0].DEPT_NAME} dan ${deptDetail.length - 1} lainnya`;
    } else {
      return 'Tidak ada pelaksana kerjasama';
    }
  }

  /**
   * Handles sort
   */
  const sort = (fieldName: string) => {
    setSortBy(fieldName);
    if (sortOrder === 'asc') setSortOrder('desc');
    else if (sortOrder === 'desc') setSortOrder('asc');
    else setSortOrder('asc');
  }

  /**
   * Renders list of documents from API calls.
   * - Should error recieved, will display error
   *   message along with refresh button.
   * - Should status is still loading, a loading
   *   message with no action will be displayed.
   * - Should data does not exist, notify user to
   *   refresh or change filter.
   * - Should data exist, returns table row.
   */
  const renderDokumenKerjasama = () => {
    if (errorMessage) {
      return (
        // <tr>
        //   <td colSpan={9} style={{textAlign: 'center'}}>
        //     Terdapat kesalahan dalam memuat data: <b>{errorMessage}</b><br/>
        //     <button className="btn btn-primary btn-xs" disabled={isLoading} onClick={props.refresh}>Coba Perbaharui Halaman</button>
        //   </td>
        // </tr>
        <div style={{textAlign: 'center'}}>
          Terdapat kesalahan dalam memuat data: <b>{errorMessage}</b><br/>
          <button className="btn btn-primary btn-xs" disabled={isLoading} onClick={props.refresh}>Coba Perbaharui Halaman</button>
        </div>
      )
    }

    if (isLoading) {
      return (
        // <tr>
        //   <td colSpan={9} style={{textAlign: 'center'}}>
        //     Sedang memuat data &nbsp; <i className="fa fa-refresh fa-spin"></i>
        //   </td>
        // </tr>
        <div style={{textAlign: 'center'}}>
          Sedang memuat data &nbsp; <i className="fa fa-refresh fa-spin"></i>
        </div>
      );
    }

    if (listDokumenKerjasama.length === 0) {
      return (
        // <tr>
        //   <td colSpan={9} style={{textAlign: 'center'}}>
        //     Data tidak ditemukan! &nbsp; <button className="btn btn-primary btn-xs" disabled={isLoading} onClick={props.refresh}>Coba Perbaharui Halaman</button> atau ganti pengaturan penyaringan.
        //   </td>
        // </tr>
        <div style={{textAlign: 'center'}}>
          Data tidak ditemukan! &nbsp; <button className="btn btn-primary btn-xs" disabled={isLoading} onClick={props.refresh}>Coba Perbaharui Halaman</button> atau ganti pengaturan penyaringan.
        </div>
      );
    }

    const items = listDokumenKerjasama.map((dokumenKerjasama, index) => {
      return {
        NO: index +1,
        id: dokumenKerjasama.DOK_ID,
        DOK_NOMOR: dokumenKerjasama.DOK_NOMOR ? dokumenKerjasama.DOK_NOMOR.trim() : '',
        // PERIODE_AWAL: moment(dokumenKerjasama.PERIODE_AWAL).format('DD MMMM YYYY'),
        PERIODE_AWAL: dokumenKerjasama.PERIODE_AWAL,
        // PERIODE_AKHIR: moment(dokumenKerjasama.PERIODE_AKHIR).format('DD MMMM YYYY'),
        PERIODE_AKHIR: dokumenKerjasama.PERIODE_AKHIR,
        DOK_JUDUL_KERJASAMA: dokumenKerjasama.DOK_JUDUL_KERJASAMA,
        PARTNER_NAME: renderCompanyCount(dokumenKerjasama.CO_DETAIL),
        STATUS_NAME: dokumenKerjasama.STATUS_DETAIL?.STATUS_NAME,
        DOK_JENIS_NAMA: dokumenKerjasama.DOK_JENIS_DETAIL?.DOK_JENIS_NAMA,
        PELAKSANA_KERJASAMA: renderDepartmentCount(dokumenKerjasama.PELAKSANA_KERJASAMA),
        RAW: dokumenKerjasama
      };
    });

    // let itemsSorted: typeof items = [];
    // const _sortBy = (sortBy || 'DOK_NOMOR') as keyof typeof items[0];
    // if (sortBy === '' || sortOrder === '') {
    //   itemsSorted = items.sort((a, b) => {
    //     const aStatus = a.STATUS_NAME || '';
    //     const bStatus = b.STATUS_NAME || '';
    //     if (a.PARTNER_NAME === b.PARTNER_NAME) {
    //       if (aStatus === bStatus) {
    //         return (a.DOK_NOMOR < b.DOK_NOMOR) ? -1 : (a.DOK_NOMOR > b.DOK_NOMOR) ? 1 : 0;
    //       } else {
    //         return (aStatus < bStatus) ? -1 : 1;
    //       }
    //     } else {
    //       return (a.PARTNER_NAME < b.PARTNER_NAME) ? -1 : 1;
    //     }
    //   });
    // } else {
    //   if (['DOK_NOMOR', 'PARTNER_NAME', 'STATUS_NAME'].includes(sortBy)) {
    //     itemsSorted = items.sort((a, b) => {
    //       const comparableA = a[_sortBy] || a.DOK_NOMOR;
    //       const comparableB = b[_sortBy] || b.DOK_NOMOR;
    //       if (sortOrder === 'asc') {
    //         if (comparableA < comparableB) return 1;
    //         else if (comparableA > comparableB) return -1;
    //         return 0;
    //       } else {
    //         if (comparableA < comparableB) return -1;
    //         else if (comparableA > comparableB) return 1;
    //         return 0;
    //       }
    //     });
    //   }
    // }

    // return itemsSorted.map((dokumenKerjasama, index) => {
    //   return (
        // <tr key={dokumenKerjasama.ID}>
        //   <td>{index + 1}</td>
        //   <td>{dokumenKerjasama.DOK_NOMOR}</td>
        //   <td>{dokumenKerjasama.PERIODE_AWAL}</td>
        //   <td>{dokumenKerjasama.PERIODE_AKHIR}</td>
        //   <td>{dokumenKerjasama.DOK_JUDUL_KERJASAMA}</td>
        //   <td>{dokumenKerjasama.PARTNER_NAME}</td>
        //   <td>{dokumenKerjasama.STATUS_NAME}</td>
        //   <td>{dokumenKerjasama.DOK_JENIS_NAMA}</td>
        //   <td>{dokumenKerjasama.PELAKSANA_KERJASAMA}</td>
        //   <td style={{ whiteSpace: 'nowrap' }}>
        //     <button disabled={isLoading} className="btn btn-primary btn-xs" onClick={() => editDokumenKerjasama(dokumenKerjasama.RAW)}>
        //       <i className="fa fa-edit" aria-hidden="true"></i>
        //     </button>
        //     &nbsp;
        //     <button disabled={isLoading || !roleMonev.read} className="btn btn-default btn-xs" onClick={() => editMonev(dokumenKerjasama.RAW)}>
        //       Kelola Kegiatan
        //     </button>
        //   </td>
        // </tr>
      // );
    // })

    // const wrapCellRenderer = (params: GridRenderCellParams) => (
    //   <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
    //     {params.value}
    //   </div>
    // );

    // const dateRenderer = (params: GridRenderCellParams) => (
    //   <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
    //     {moment(params.value).format('DD MMMM YYYY')}
    //   </div>
    // );

    // const actionRenderer = (params: GridRenderCellParams) => (
    //   <>
    //     <button disabled={isLoading} className="btn btn-primary btn-xs" onClick={() => editDokumenKerjasama(params.row.RAW)}>
    //       <i className="fa fa-edit" aria-hidden="true"></i>
    //     </button>
    //     &nbsp;
    //     <button disabled={isLoading || !roleMonev.read} className="btn btn-primary btn-xs" onClick={() => editMonev(params.row.RAW)}>
    //       <AssignmentOutlinedIcon style={{marginBottom: '-3px'}} />
    //     </button>
    //   </>
    // )

    // const columns: GridColDef[] = [
    //   { field: 'NO', headerName: 'No', width: 32, minWidth: 32, renderCell: wrapCellRenderer },
    //   { field: 'DOK_NOMOR', headerName: 'Nomor Dokumen', width: 190, renderCell: wrapCellRenderer },
    //   { field: 'PERIODE_AWAL', headerName: 'Tanggal Mulai', width: 95, renderCell: dateRenderer },
    //   { field: 'PERIODE_AKHIR', headerName: 'Tanggal Selesai', width: 100, renderCell: dateRenderer },
    //   { field: 'DOK_JUDUL_KERJASAMA', headerName: 'Judul Dokumen', width: 150, renderCell: wrapCellRenderer },
    //   { field: 'PARTNER_NAME', headerName: 'Partner', width: 110, renderCell: wrapCellRenderer },
    //   { field: 'STATUS_NAME', headerName: 'Status', width: 70, renderCell: wrapCellRenderer },
    //   { field: 'DOK_JENIS_NAMA', headerName: 'Jenis Dokumen', width: 120, renderCell: wrapCellRenderer },
    //   { field: 'PELAKSANA_KERJASAMA', headerName: 'Pelaksana Kerjasama', width: 130, renderCell: wrapCellRenderer },
    //   { field: 'actions', headerName: '', width: 70, renderCell: actionRenderer }
    // ];

    const getHeaderProps = (width: string, padding: string) => {
      return {
        style: {
          width: width,
          maxWidth: width,
          padding: padding
        },
      };
    };

    const getCellProps = (width: string, padding: string, align: string) => {
      return {
        style: {
          width: width,
          maxWidth: width,
          padding: padding,
          fontSize: '12px',
          textAlign: `${align}`
        },
      };
    };

    const dateRenderer = (value: string) => (
        moment(value).format('DD MMMM YYYY')
    );

    const actionRenderer = (value: any) => (
      <div style={{display: 'flex'}}>
        <Tooltip title="Ubah"
          slots={{
            transition: Zoom,
          }} 
          componentsProps={{
            tooltip: {
              sx: {
                fontSize: '11px'
              },
            },
          }}>
          <button disabled={isLoading} className="btn btn-primary btn-xs" onClick={() => editDokumenKerjasama(value)}>
            <i className="fa fa-edit" aria-hidden="true"></i>
          </button>
        </Tooltip>
        &nbsp;
        <Tooltip title="Kelola Kegiatan"
          slots={{
            transition: Zoom,
          }} 
          componentsProps={{
            tooltip: {
              sx: {
                fontSize: '11px'
              },
            },
          }}>
          <button disabled={isLoading || !roleMonev.read} className="btn btn-primary btn-xs" onClick={() => editMonev(value)} style={{marginRight: '10px'}}>
            <AssignmentOutlinedIcon style={{marginBottom: '-3px'}} />
          </button>
        </Tooltip>
      </div>
    )

    const columns = [
        { 
          name: "NO",
          label: "No",
          options: {
            filter: false,
            sort: true,
            setCellHeaderProps: () => getHeaderProps('50px', '5px 0 5px 0'),
            setCellProps: () => getCellProps('50px', '5px 0 5px 10px', 'left')
          },
        },
        { 
          name: "DOK_NOMOR",
          label: "Nomor Dokumen",
          options: {
            filter: true,
            sort: true,
            setCellHeaderProps: () => getHeaderProps('14%', '5px 0 5px 0'),
            setCellProps: () => getCellProps('14%', '5px 0 5px 10px', 'left')
          },
        },
        { 
          name: "PERIODE_AWAL",
          label: "Tanggal Mulai",
          options: {
            filter: true,
            sort: true,
            setCellHeaderProps: () => getHeaderProps('12%', '5px 0 5px 0'),
            setCellProps: () => getCellProps('12%', '5px 0 5px 10px', 'left'),
            customBodyRender: dateRenderer
          },
        },
        { 
          name: "PERIODE_AKHIR",
          label: "Tanggal Selesai",
          options: {
            filter: true,
            sort: true,
            setCellHeaderProps: () => getHeaderProps('12%', '5px 0 5px 0'),
            setCellProps: () => getCellProps('12%', '5px 0 5px 10px', 'left'),
            customBodyRender: dateRenderer
          },
        },
        { 
          name: "DOK_JUDUL_KERJASAMA",
          label: "Judul Dokumen",
          options: {
            filter: true,
            sort: true,
            setCellHeaderProps: () => getHeaderProps('16%', '5px 0 5px 0'),
            setCellProps: () => getCellProps('16%', '5px 0 5px 10px', 'left')
          },
        },
        { 
          name: "PARTNER_NAME",
          label: "Partner",
          options: {
            filter: true,
            sort: true,
            setCellHeaderProps: () => getHeaderProps('10%', '5px 0 5px 0'),
            setCellProps: () => getCellProps('10%', '5px 0 5px 10px', 'left')
          },
        },
        { 
          name: "STATUS_NAME",
          label: "Status",
          options: {
            filter: true,
            sort: true,
            setCellHeaderProps: () => getHeaderProps('5%', '5px 0 5px 0'),
            setCellProps: () => getCellProps('5%', '5px 0 5px 10px', 'left')
          },
        },
        { 
          name: "DOK_JENIS_NAMA",
          label: "Jenis Dokumen",
          options: {
            filter: true,
            sort: true,
            setCellHeaderProps: () => getHeaderProps('10%', '5px 0 5px 0'),
            setCellProps: () => getCellProps('10%', '5px 0 5px 10px', 'left')
          },
        },
        { 
          name: "PELAKSANA_KERJASAMA",
          label: "Pelaksana Kerjasama",
          options: {
            filter: true,
            sort: true,
            setCellHeaderProps: () => getHeaderProps('10%', '5px 0 5px 0'),
            setCellProps: () => getCellProps('10%', '5px 0 5px 10px', 'left')
          },
        },
        { 
          name: "RAW",
          label: " ",
          options: {
            filter: false,
            sort: false,
            download: false,
            setCellHeaderProps: () => getHeaderProps('5%', '5px 0 5px 0'),
            setCellProps: () => getCellProps('5%', '5px 0 5px 10px', 'left'),
            customBodyRender: actionRenderer
          },
        }
      ];
      

    return (
      // <Table data={items} col={columns} />
      <Datatables data={items} col={columns} title={'Daftar Dokumen Kerjasama'} />
    )
  }

  const renderSortIcon = (fieldName: string) => {
    if (fieldName === sortBy) {
      if (sortOrder === 'asc') return 'fa fa-arrow-down';
      else if (sortOrder === 'desc') return 'fa fa-arrow-up';  
    }
    return 'fa fa-sort';
  }

  // fa-arrow-down

  return (
    <div style={{padding: '0 10px 10px 10px'}}>
      { renderDokumenKerjasama() }
    </div>
    // <div className="box-body table-responsive no-padding">
    //   <table className="table table-hover" id="main-table">
    //     <thead>
    //       <tr>
    //         <th>#</th>
    //         <th style={{ whiteSpace: 'nowrap' }}>
    //           No. Dokumen &nbsp;
    //           <button disabled={isLoading} className="btn btn-primary btn-xs" onClick={() => sort('DOK_NOMOR')}>
    //             <i className={`${renderSortIcon('DOK_NOMOR')}`} aria-hidden="true"></i>
    //           </button>
    //         </th>
    //         <th>Tanggal Mulai Kerjasama</th>
    //         <th>Tanggal Selesai Kerjasama</th>
    //         <th>Judul Dokumen</th>
    //         <th style={{ whiteSpace: 'nowrap' }}>
    //           Partner &nbsp;
    //           <button disabled={isLoading} className="btn btn-primary btn-xs" onClick={() => sort('PARTNER_NAME')}>
    //             <i className={`${renderSortIcon('PARTNER_NAME')}`} aria-hidden="true"></i>
    //           </button>
    //         </th>
    //         <th style={{ whiteSpace: 'nowrap' }}>
    //           Status &nbsp;
    //           <button disabled={isLoading} className="btn btn-primary btn-xs" onClick={() => sort('STATUS_NAME')}>
    //             <i className={`${renderSortIcon('STATUS_NAME')}`} aria-hidden="true"></i>
    //           </button>
    //         </th>
    //         <th>Jenis Dokumen</th>
    //         <th>Pelaksana Kerjasama</th>
    //         <th></th>
    //       </tr>
    //     </thead>
    //     <tbody>
    //       { renderDokumenKerjasama() }
    //     </tbody>
    //   </table>
    // </div>
  );
}

export default DokumenKerjasamaListPageList;